import { ArrowUpRight, Bookmark, ChevronDown, ChevronLeft, ChevronRight, MapPin, Search, SlidersHorizontal } from "lucide-react"
import { DashboardShell } from "@/components/dashboard-shell"
import { Link, useLocation, useNavigate } from "react-router"
import { useState, useEffect } from "react"

import { Badge } from "@/components/ui/badge"
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Slider } from "@/components/ui/slider"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { api, getUserProfile } from "@/lib/api"

interface ProjectDeveloper {
  id: string
  role: string
  developer: { id: string; name: string }
}

interface Project {
  id: string
  name: string
  technology: string | null
  lifecycleStage: string | null
  region: string | null
  country: string | null
  planningAuthority: string | null
  developers: ProjectDeveloper[]
}

interface Pagination {
  total: number
  perPage: number
  totalPages: number
  currentPage: number
  hasNextPage: boolean
}

interface BookmarkItem {
  id: string
  user_id: string
  target_id: string
  type: string
  project?: Project
}

const REGIONS_BY_COUNTRY: Record<string, string[]> = {
  England: [
    "East Midlands",
    "Eastern",
    "London",
    "North East",
    "North West",
    "South East",
    "South West",
    "West Midlands",
    "Yorkshire and Humber",
    "Offshore"
  ],
  "Northern Ireland": ["Northern Ireland", "Offshore"],
  Scotland: ["Scotland", "Offshore"],
  Wales: ["Wales", "Offshore"]
}

const COUNTRIES = Object.keys(REGIONS_BY_COUNTRY)

const TECHNOLOGIES = [
  "Advanced Conversion Technologies",
  "Anaerobic Digestion",
  "Battery",
  "Biomass (co-firing)",
  "Biomass (dedicated)",
  "Compressed Air Energy Storage",
  "EfW Incineration",
  "Flywheels",
  "Fuel Cell (Hydrogen)",
  "Geothermal",
  "Hot Dry Rocks (HDR)",
  "Hydrogen",
  "Landfill Gas",
  "Large Hydro",
  "Liquid Air Energy Storage",
  "Pumped Storage Hydroelectricity",
  "Sewage Sludge Digestion",
  "Shoreline Wave",
  "Small Hydro",
  "Solar Photovoltaics",
  "Tidal Lagoon",
  "Tidal Stream",
  "Unknown",
  "Wind Offshore",
  "Wind Onshore"
]

const STATUSES = [
  "Abandoned",
  "Appeal Granted",
  "Appeal Lodged",
  "Appeal Refused",
  "Appeal Withdrawn",
  "Decommissioned",
  "No Application Required",
  "Operational",
  "Planning Application Submitted",
  "Planning Application Withdrawn",
  "Planning Permission Expired",
  "Planning Permission Granted",
  "Planning Permission Granted ",
  "Planning Permission Refused",
  "Revised",
  "Secretary of State - Granted",
  "Secretary of State - Refusal",
  "Under Construction"
]

const ITEMS_PER_PAGE = 20

export default function ProjectsPage() {
  const userProfile = getUserProfile()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const activeTab = pathname === "/dashboard/watchlist" ? "watchlist" : "all"

  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [regionFilter, setRegionFilter] = useState("all")
  const [countryFilter, setCountryFilter] = useState("all")
  const [technologyFilter, setTechnologyFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [capacityRange, setCapacityRange] = useState<number[]>([0, 1000])
  const [currentPage, setCurrentPage] = useState(1)

  const [projects, setProjects] = useState<Project[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [projectsLoading, setProjectsLoading] = useState(false)

  // projectId → bookmarkId
  const [bookmarkMap, setBookmarkMap] = useState<Record<string, string>>({})
  const [watchlistProjects, setWatchlistProjects] = useState<Project[]>([])
  const [watchlistLoading, setWatchlistLoading] = useState(false)

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400)
    return () => clearTimeout(t)
  }, [searchQuery])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, regionFilter, countryFilter, technologyFilter, statusFilter, capacityRange])

  // Fetch all projects
  useEffect(() => {
    if (activeTab !== "all") return

    const params = new URLSearchParams({ limit: String(ITEMS_PER_PAGE), page: String(currentPage) })
    if (debouncedSearch) params.set("name", debouncedSearch)
    if (regionFilter !== "all") params.set("region", regionFilter)
    if (countryFilter !== "all") params.set("country", countryFilter)
    if (technologyFilter !== "all") params.set("technology", technologyFilter)
    if (statusFilter.length > 0) params.set("lifecycle_stage", statusFilter.join(","))
    if (capacityRange[0] > 0) params.set("min_capacity", String(capacityRange[0]))
    if (capacityRange[1] < 1000) params.set("max_capacity", String(capacityRange[1]))

    setProjectsLoading(true)
    api
      .get<{ data: { data: Project[]; pagination: Pagination } }>(`/projects?${params.toString()}`)
      .then((res) => {
        setProjects(res.data.data ?? [])
        setPagination(res.data.pagination ?? null)
      })
      .catch(() => {
        setProjects([])
        setPagination(null)
      })
      .finally(() => setProjectsLoading(false))
  }, [activeTab, debouncedSearch, regionFilter, countryFilter, technologyFilter, statusFilter, capacityRange, currentPage])

  // Fetch bookmarks on mount and when user changes
  useEffect(() => {
    if (!userProfile?.id) return

    setWatchlistLoading(true)
    api
      .get<{ data: { data: BookmarkItem[] } }>(`/bookmarks/user?user_id=${userProfile.id}&type=project`)
      .then((res) => {
        const items = res.data.data ?? []
        const map: Record<string, string> = {}
        const projectList: Project[] = []

        items.forEach((item) => {
          map[item.target_id] = item.id
          if (item.project) projectList.push(item.project)
        })

        setBookmarkMap(map)
        setWatchlistProjects(projectList)
      })
      .catch(() => {})
      .finally(() => setWatchlistLoading(false))
  }, [userProfile?.id])

  const toggleBookmark = async (project: Project) => {
    if (!userProfile?.id) return

    const existingId = bookmarkMap[project.id]

    if (existingId) {
      await api.delete(`/bookmarks/${existingId}`).catch(() => {})
      setBookmarkMap((prev) => {
        const next = { ...prev }
        delete next[project.id]
        return next
      })
      setWatchlistProjects((prev) => prev.filter((p) => p.id !== project.id))
    } else {
      const res = await api
        .post<{ data: BookmarkItem }>("/bookmarks", {
          user_id: userProfile.id,
          target_id: project.id,
          type: "project",
        })
        .catch(() => null)

      if (res?.data) {
        setBookmarkMap((prev) => ({ ...prev, [project.id]: res.data.id }))
        setWatchlistProjects((prev) => [...prev, project])
      }
    }
  }

  const totalPages = pagination?.totalPages ?? 1

  const PaginationBar = ({ total }: { total: number }) => {
    if (totalPages <= 1) return null

    const start = (currentPage - 1) * ITEMS_PER_PAGE + 1
    const end = Math.min(currentPage * ITEMS_PER_PAGE, total)

    const pageButtons = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
      if (totalPages <= 5) return i + 1
      if (currentPage <= 3) return i + 1
      if (currentPage >= totalPages - 2) return totalPages - 4 + i
      return currentPage - 2 + i
    })

    return (
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {start}–{end} of {total} projects
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <div className="flex items-center gap-1">
            {pageButtons.map((num) => (
              <Button
                key={num}
                variant={currentPage === num ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentPage(num)}
                className="w-9"
              >
                {num}
              </Button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    )
  }

  const ProjectTable = ({ rows, loading }: { rows: Project[]; loading: boolean }) => (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-65">Project Name</TableHead>
                <TableHead>Technology</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Region
                  </span>
                </TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Developers</TableHead>
                <TableHead className="text-center w-[80px]">Watchlist</TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <TableCell key={j}>
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-10">
                    No projects found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((project) => {
                  const isBookmarked = Boolean(bookmarkMap[project.id])
                  const developerNames = project.developers?.map((d) => d.developer.name).join(", ") || "—"

                  return (
                    <TableRow key={project.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <Link to={`/dashboard/projects/${project.id}`} className="hover:text-primary">
                          {project.name}
                        </Link>
                      </TableCell>
                      <TableCell>{project.technology ?? "—"}</TableCell>
                      <TableCell>
                        {project.lifecycleStage ? (
                          <Badge variant="secondary" className="capitalize">
                            {project.lifecycleStage}
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{project.region ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{project.country ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[180px] truncate">{developerNames}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault()
                            toggleBookmark(project)
                          }}
                          className="h-8 w-8"
                        >
                          <Bookmark
                            className={`h-4 w-4 transition-colors ${isBookmarked ? "fill-primary text-primary" : ""}`}
                          />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Link to={`/dashboard/projects/${project.id}`}>
                          <Button variant="ghost" size="icon">
                            <ArrowUpRight className="h-4 w-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!loading && pagination && <PaginationBar total={pagination.total} />}
    </div>
  )

  return (
    <DashboardShell>
      <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{activeTab === "watchlist" ? "Watchlist" : "Projects"}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-6 p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {activeTab === "watchlist" ? "Watchlist" : "Projects"}
            </h1>
            <p className="text-muted-foreground">
              {activeTab === "watchlist"
                ? "Your saved projects"
                : "Browse and manage renewable energy projects"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon">
                  <SlidersHorizontal className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-4">
                  <h4 className="font-medium">Filters</h4>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Country</label>
                    <Select
                      value={countryFilter}
                      onValueChange={(val) => {
                        setCountryFilter(val)
                        setRegionFilter("all")
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All countries" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All countries</SelectItem>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {countryFilter !== "all" && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Region</label>
                      <Select value={regionFilter} onValueChange={setRegionFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="All regions" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All regions</SelectItem>
                          {REGIONS_BY_COUNTRY[countryFilter].map((r) => (
                            <SelectItem key={r} value={r}>
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Technology</label>
                    <Select value={technologyFilter} onValueChange={setTechnologyFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="All technologies" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All technologies</SelectItem>
                        {TECHNOLOGIES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Status</label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full justify-between font-normal">
                          <span className="truncate">
                            {statusFilter.length === 0
                              ? "All statuses"
                              : statusFilter.length === 1
                                ? statusFilter[0]
                                : `${statusFilter.length} selected`}
                          </span>
                          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-72 max-h-72 overflow-y-auto" align="start">
                        {STATUSES.map((status) => (
                          <DropdownMenuCheckboxItem
                            key={status}
                            checked={statusFilter.includes(status)}
                            onSelect={(e) => e.preventDefault()}
                            onCheckedChange={(checked) => {
                              setStatusFilter((prev) =>
                                checked ? [...prev, status] : prev.filter((s) => s !== status)
                              )
                            }}
                          >
                            {status}
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">Capacity Range (MW)</label>
                      <span className="text-sm text-muted-foreground">
                        {capacityRange[0]}–{capacityRange[1]} MW
                      </span>
                    </div>
                    <Slider
                      min={0}
                      max={1000}
                      step={10}
                      value={capacityRange}
                      onValueChange={setCapacityRange}
                      className="w-full"
                    />
                  </div>

                  <Button
                    variant="outline"
                    className="w-full bg-transparent"
                    onClick={() => {
                      setRegionFilter("all")
                      setCountryFilter("all")
                      setTechnologyFilter("all")
                      setStatusFilter([])
                      setCapacityRange([0, 1000])
                      setSearchQuery("")
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(val) => {
            setCurrentPage(1)
            navigate(val === "watchlist" ? "/dashboard/watchlist" : "/dashboard/projects")
          }}
        >
          <TabsList>
            <TabsTrigger value="all">
              All Projects
              {pagination && (
                <Badge className="ml-2">
                  {pagination.total.toLocaleString()}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="watchlist">
              Watchlist
              <Badge className="ml-2">
                {watchlistProjects.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <ProjectTable rows={projects} loading={projectsLoading} />
          </TabsContent>

          <TabsContent value="watchlist" className="mt-6">
            <ProjectTable rows={watchlistProjects} loading={watchlistLoading} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardShell>
  )
}
